import QueryString from 'qs'
import { useRouter } from 'next/router'
import { useTranslations } from 'next-intl'
import { addHours, format, isSameMonth } from 'date-fns'
import { Box, Heading, Icon, Paragraph } from '@alto-avios/alto-ui'
import { z } from 'zod'
import {
  HotelDestinationSchema,
  HotelResultsFormState,
  RoomSearchParamSchema,
} from '@reward-platform/ancillaries-schemas/hotel'
import { usePlatformConfig, usePlatformRoutes } from '~/context/platform'
import { useTagManagerContext } from '~/context/analytics'
import { isClient } from '~/utils/envChecks'
import { preserveDateWhenConvertedToUTC } from '~/utils/datetime'
import { Room } from '../SearchHotelForm/TravelerRoomPicker/TravelerRoomPicker.types'
import { stringifyHotelSearchFormToQuery } from '../SearchHotelForm/useHotelSearchParams'
import { LOCAL_STORAGE_HOTELS_RECENT_SEARCHES } from './RecentSearches.storage'

import styles from './RecentSearches.module.scss'

export type RecentSearch = Pick<HotelResultsFormState, 'destination' | 'rooms'> & {
  startDateTime: string | Date
  endDateTime: string | Date
}

const RecentSearchSchema = z.object({
  destination: HotelDestinationSchema,
  rooms: z.array(RoomSearchParamSchema),
  startDateTime: z.union([z.string(), z.date()]),
  endDateTime: z.union([z.string(), z.date()]),
})

const MAX_COUNT_RECENT_SEARCHES = 4

export { LOCAL_STORAGE_HOTELS_RECENT_SEARCHES } from './RecentSearches.storage'

const filterValidSearch = (entry: RecentSearch) => {
  if (!entry.destination?.fullName || !entry.startDateTime || !entry.endDateTime) {
    return false
  }
  const minStartDateTime = addHours(new Date(), 27)
  return (
    new Date(entry.startDateTime) > minStartDateTime &&
    new Date(entry.endDateTime) > new Date(entry.startDateTime)
  )
}

export const getRecentSearchesFromLocalStorage = (): RecentSearch[] => {
  if (!isClient()) {
    return []
  }
  const data = localStorage.getItem(LOCAL_STORAGE_HOTELS_RECENT_SEARCHES)
  if (!data) {
    return []
  }
  try {
    const raw = JSON.parse(data)
    if (!Array.isArray(raw)) {
      return []
    }
    return raw
      .flatMap((item) => {
        const result = RecentSearchSchema.safeParse(item)
        return result.success ? [result.data] : []
      })
      .filter((x) => filterValidSearch(x))
  } catch {
    return []
  }
}

export const addSearchToLocalStorage = (data: RecentSearch) => {
  if (!isClient()) {
    return
  }
  const currentSearches = getRecentSearchesFromLocalStorage()

  const foundIndex = currentSearches.findIndex((x) => JSON.stringify(x) === JSON.stringify(data))
  if (foundIndex > -1) {
    currentSearches.splice(foundIndex, 1)
  }

  if (currentSearches.length > MAX_COUNT_RECENT_SEARCHES - 1) {
    currentSearches.shift()
  }
  currentSearches.push(data)

  localStorage.setItem(LOCAL_STORAGE_HOTELS_RECENT_SEARCHES, JSON.stringify(currentSearches))
}

export default function RecentSearches() {
  const data = getRecentSearchesFromLocalStorage()

  const recentSearches = [...data].reverse()
  const router = useRouter()
  const { createRoutePath } = usePlatformRoutes()
  const { opCo } = usePlatformConfig()

  const t = useTranslations()
  const { pushEventToDataLayer } = useTagManagerContext()

  const formatTravellers = (rooms: Room[]) => {
    const adultCount = rooms.reduce((prev, crt) => prev + crt.adults, 0)
    const childCount = rooms.reduce((prev, crt) => prev + crt.children, 0)
    const travellersCount = adultCount + childCount
    const formattedTravellers = t('common.labels.traveller_count', { count: travellersCount })
    const formattedRoomCount = t('common.labels.room_count', { count: rooms.length })
    return ` ${formattedTravellers}, ${formattedRoomCount}`
  }

  const formatDates = (startDateTime: string | Date, endDateTime: string | Date) => {
    const startDate = new Date(startDateTime)
    const endDate = new Date(endDateTime)
    if (isSameMonth(startDate, endDate)) {
      return `${format(startDate, 'dd')} - ${format(endDate, 'dd LLL')}, `
    }
    return `${format(startDate, 'dd LLL')} - ${format(endDate, 'dd LLL')}, `
  }

  const computeSearch = (search: RecentSearch) => {
    pushEventToDataLayer('navigation', {
      nav_element: 'hotels recent searches',
      nav_type: 'cta',
      nav_action: `${search.destination.fullName} | ${formatDates(
        search.startDateTime,
        search.endDateTime
      )} | ${formatTravellers(search.rooms)}`,
      referrer: document.referrer,
      departure_date: format(new Date(search.endDateTime), 'yyyy-MM-dd'),
      arrival_date: format(new Date(search.startDateTime), 'yyyy-MM-dd'),
      origin: 'not_applicable',
      destination: search.destination.fullName,
    })

    const baseQuery = stringifyHotelSearchFormToQuery({
      ...search,
      startDateTime: preserveDateWhenConvertedToUTC(new Date(search.startDateTime)),
      endDateTime: preserveDateWhenConvertedToUTC(new Date(search.endDateTime)),
    })
    const query = QueryString.stringify({ ...QueryString.parse(baseQuery), opco: opCo })
    router.push({
      pathname: createRoutePath('/hotels/results'),
      query,
    })

    addSearchToLocalStorage(search)
  }

  return (
    <Box width="100%" flexDirection="column" padding="lg" gap="md" position="relative">
      <Heading as="h4">{t('common.labels.recent_searches')}</Heading>
      <Box flexDirection="column" width="100%">
        {recentSearches.map((search) => (
          <button
            type="button"
            key={JSON.stringify(search)}
            data-testid="recent-search"
            onClick={() => computeSearch(search)}
            className={styles.buttonContainer}
          >
            <Icon iconName="clock" />
            <Box flexDirection="column" textAlign="left">
              <Paragraph size="lg">{search.destination.fullName}</Paragraph>
              <Paragraph>
                {formatDates(search.startDateTime, search.endDateTime)}
                {formatTravellers(search.rooms)}
              </Paragraph>
            </Box>
          </button>
        ))}
      </Box>
    </Box>
  )
}
